import React, { useEffect, useState } from "react";
import { usePrivacyCenterSettings } from "../../../components/superorg/settings/PrivacyCenterSettings";
import { CodeSnippet } from "../../../components/organisms/CodeSnippet";
import { getPrivacyCenterCnameValue } from "../../../util/privacyCenterUtil";
import { Field, Form } from "react-final-form";
import { Api } from "../../../../common/service/Api";
import { Loading } from "../../../components";
import { DescriptionCheckbox } from "../../../components/input/DescriptionCheckbox";
import { usePrimaryOrganization, useSelf } from "../../../hooks";
import { useCreateDefaultCollectionGroups } from "../../../hooks/useCollectionGroups";
import clsx from "clsx";
import { StepNumberCircle } from "./EmailSetupPage";
import { Button, OnboardingOrganizationDropdown, TextInput } from "@truevault/aquilo";
import { useHistory } from "react-router";
import { Routes } from "../../../root/routes";
import { PrivacyCenterDto } from "../../../../common/service/server/dto/PrivacyCenterDto";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { OmniBarProvider } from "../../../../common/hooks/useOmniBar";
import { OmniBar } from "../../../components/dialog/OmniBar";

export function WebsiteSetupPage() {
  const history = useHistory();

  const { privacyCenter } = usePrivacyCenterSettings();

  const [org] = usePrimaryOrganization();
  const organizationId = org?.id;
  const [self] = useSelf();

  const { create: createDefaultCollectionGroups } =
    useCreateDefaultCollectionGroups(organizationId);

  const [showVerifySubdomainStep, setShowVerifySubdomainStep] = useState(false);

  const [skipCnameVerify, setSkipCnameVerify] = useState<boolean>(false);

  const onHandleSkip = (checked) => {
    setSkipCnameVerify(checked);
  };

  const [privacyCenterDomain, setPrivacyCenterDomain] = useState<string>("");
  useEffect(() => {
    if (privacyCenter?.domains?.length) {
      setPrivacyCenterDomain(privacyCenter.domains[0].customDomain);
    }
  }, [privacyCenter]);

  let isVerifySubdomainStepComplete = Boolean(privacyCenterDomain) || skipCnameVerify;

  if (!privacyCenter) {
    return <Loading />;
  }

  return (
    <OmniBarProvider>
      <OmniBar></OmniBar>
      <div className="tw-boundary">
        <div className="flex justify-end pr-[32px]">
          <div>
            <OnboardingOrganizationDropdown
              orgName={org?.name}
              userDisplayName={`${self?.firstName} ${self?.lastName}`}
              showSettings={false}
            />
          </div>
        </div>
        <div
          className="flex items-center mx-auto pt-[10px] z-[2]"
          style={{ flexDirection: "column" }}
        >
          <img
            src="/assets/images/nav-icon-truevault.png"
            alt="TrueVault Logo"
            className="w-[40px] h-[40px]"
          />
          <div className="mt-[12px] text-primary-800 font-bold text-[30px]">
            Now let's set up a custom domain for your Privacy Center
          </div>
          <div className="mt-[12px] max-w-[60ch] text-primary-800 font-medium text-[20px] text-center">
            Your Privacy Center will host your Privacy Policy and request forms.
          </div>
          <div
            className={clsx(
              "w-[890px] rounded-[24px] bg-white",
              "p-[36px] mt-[24px]",
              "border border-solid border-neutral-300",
              "shadow-[0px_2px_12px_0px_rgba(0,0,0,0.12)]",
            )}
          >
            {showVerifySubdomainStep ? (
              <>
                <div className="mb-[12px]">
                  <div className="text-[18px] font-medium text-neutral-900">
                    Verify your new subdomain
                  </div>
                  <div className="mt-[6px] text-[14px] text-neutral-900 font-normal">
                    Once your CNAME is set up, click below to verify that your subdomain is working.
                  </div>
                </div>
                <VerifySubdomain
                  privacyCenter={privacyCenter}
                  privacyCenterDomain={privacyCenterDomain}
                  setPrivacyCenterDomain={setPrivacyCenterDomain}
                  onHandleSkip={onHandleSkip}
                />
              </>
            ) : (
              <CustomDomainInstructions privacyCenter={privacyCenter} />
            )}
          </div>
          <div
            className="mt-[24px] flex justify-center items-center"
            style={{ flexDirection: "column" }}
          >
            <div>
              <Button
                color="primary"
                disabled={showVerifySubdomainStep ? !isVerifySubdomainStepComplete : false}
                onClick={async () => {
                  if (showVerifySubdomainStep) {
                    await createDefaultCollectionGroups();
                    history.push(Routes.getCompliant.businessSurveyAquilo);
                  } else {
                    setShowVerifySubdomainStep(true);
                  }
                }}
              >
                Continue
              </Button>
            </div>
          </div>
          <a href={Routes.getCompliant.privacyCenter.emailSetup} className="mt-[12px]">
            <div className="flex items-center">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.70711 14.7071C7.31658 15.0976 6.68342 15.0976 6.2929 14.7071L2.29289 10.7071C1.90237 10.3166 1.90237 9.68342 2.29289 9.29289L6.29289 5.29289C6.68342 4.90237 7.31658 4.90237 7.70711 5.29289C8.09763 5.68342 8.09763 6.31658 7.70711 6.70711L5.41421 9L17 9C17.5523 9 18 9.44771 18 10C18 10.5523 17.5523 11 17 11L5.41421 11L7.70711 13.2929C8.09763 13.6834 8.09763 14.3166 7.70711 14.7071Z"
                  fill="#B8B5C5"
                />
              </svg>
              <div className="ml-[6px] text-neutral-500 text-[14px] font-medium">
                Back to Email Setup
              </div>
            </div>
          </a>
        </div>
      </div>
    </OmniBarProvider>
  );
}

function CustomDomainInstructions({ privacyCenter }: { privacyCenter: PrivacyCenterDto }) {
  return (
    <>
      <div className="text-[18px] font-medium leading-[28px] text-neutral-900">
        Follow the instructions below to set up your custom domain for your Privacy Center. First
        time adding a subdomain?{" "}
        <a
          href="https://knowledge.truevault.com/article/212-how-to-set-up-a-custom-domain-using-a-cname"
          target="_blank"
          rel="noreferrer"
          className="text-blue-600 underline"
        >
          Here's a how-to guide.
        </a>
      </div>
      <CustomDomainStepByStepInstructions privacyCenter={privacyCenter} />
      <div className="mt-[24px] text-[18px] font-medium leading-[28px] text-neutral-900">
        Have a developer? Share this with them:
      </div>
      <div className="mt-[12px] space-y-[24px] text-[14px]">
        <div className="flex gap-[12px]">
          <div>
            <StepNumberCircle text="1" />
          </div>
          <div className="flex w-full">
            <span className="mr-[12px]">
              <strong>CNAME Host Name</strong>:{" "}
            </span>
            <CodeSnippet inline={true}>privacy</CodeSnippet>
          </div>
        </div>
        <div className="flex gap-[12px]">
          <div>
            <StepNumberCircle text="2" />
          </div>
          <div className="flex w-full">
            <span className="mr-[12px]">
              <strong>CNAME Value</strong>:{" "}
            </span>
            <CodeSnippet inline={true}>{getPrivacyCenterCnameValue(privacyCenter)}</CodeSnippet>
          </div>
        </div>
      </div>
    </>
  );
}

export function CustomDomainIntroInstructions() {
  return (
    <div className="text-[18px] font-medium leading-[28px] text-neutral-900">
      Follow the instructions below to set up your custom domain for your Privacy Center. First time
      adding a subdomain?{" "}
      <a
        href="https://knowledge.truevault.com/article/212-how-to-set-up-a-custom-domain-using-a-cname"
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 underline"
      >
        Here's a how-to guide.
      </a>
    </div>
  );
}

export function CustomDomainStepByStepInstructions({
  privacyCenter,
  stepNumberBgColor,
  stepNumberTextColor,
}: {
  privacyCenter: PrivacyCenterDto;
  stepNumberBgColor?: string; // tailwind class name, e.g. "bg-primary-100"
  stepNumberTextColor?: string; // tailwind class name, e.g. "text-primary-600"
}) {
  return (
    <div className="mt-[12px] space-y-[12px] text-[14px]">
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="1" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>
          Login to your domain host (for example: Google Domains, GoDaddy, Hover and similar).
        </div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="2" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>Click on your company's domain.</div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="3" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>
          Find the menu which says <strong>DNS</strong> or <strong>DNS Records</strong> and click on
          it.
        </div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="4" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>
          Click <strong>Add New Record</strong> (it might say “Create new record” or something
          similar).
        </div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="5" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>
          Select <strong>CNAME</strong> as the <strong>Type</strong>.
        </div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="6" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div className="flex w-full">
          <span className="mr-[12px]">
            At <strong>CNAME Host Name</strong>:{" "}
          </span>
          <CodeSnippet inline={true}>privacy</CodeSnippet>
        </div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="7" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>
          <div>
            Copy and paste the value below under <strong>Value</strong> (it might also be called{" "}
            <strong>Data</strong> or <strong>CNAME Destination</strong>).
          </div>
          <div className="flex w-full">
            <span className="mr-[12px]">
              <strong>CNAME Value</strong>:{" "}
            </span>
            <CodeSnippet inline={true}>{getPrivacyCenterCnameValue(privacyCenter)}</CodeSnippet>
          </div>
        </div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="8" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>
          Click <strong>Save</strong>.
        </div>
      </div>
    </div>
  );
}

export function VerifySubdomain({
  privacyCenter,
  privacyCenterDomain,
  setPrivacyCenterDomain,
  inputWidth,
  maxWidth,
  onHandleSkip,
  onSuccess,
  onError,
}: {
  privacyCenter: PrivacyCenterDto;
  privacyCenterDomain: string;
  setPrivacyCenterDomain: (domain: string) => void;
  inputWidth?: string;
  maxWidth?: string;
  onHandleSkip?: (checked: boolean) => void; // if undefined/null, then won't show checkbox to skip if subdomain verification fails
  onSuccess?: () => void;
  onError?: () => void;
}) {
  const [subdomainError, setSubdomainError] = useState<boolean | string>("");
  const [subdomainSuccessful, setSubdomainSuccessful] = useState<boolean>(false);
  const [subdomainLoading, setSubdomainLoading] = useState<boolean>(false);

  const verifyPrivacyCenterDomain = async (domain: string): Promise<void> => {
    setSubdomainLoading(true);
    setSubdomainSuccessful(false);
    setSubdomainError("");
    const res = await Api.privacyCenter.verifyPrivacyCenterDomain(
      privacyCenter.organizationId,
      privacyCenter.id,
      domain,
    );
    setSubdomainLoading(false);
    if (res) {
      setPrivacyCenterDomain(domain);
      setSubdomainSuccessful(true);

      if (onSuccess) {
        onSuccess();
      }
    } else {
      setSubdomainError(res === false ? domain : true);

      if (onError) {
        onError();
      }
    }
  };

  return (
    <>
      <Form
        initialValues={{ domain: privacyCenterDomain || "" }}
        onSubmit={(values) => {
          verifyPrivacyCenterDomain(values.domain);
        }}
        render={({ handleSubmit, values, dirty }) => {
          return (
            <form onSubmit={handleSubmit}>
              <div className={clsx(maxWidth)}>
                <div className="flex-row">
                  <Field name="domain">
                    {(props) => (
                      <TextInput
                        field="domain"
                        onChange={props.input.onChange}
                        defaultValue={privacyCenterDomain}
                        disabled={subdomainLoading}
                        inputWidth={inputWidth || "w-[550px]"}
                        inputTextSize="text-[14px] text-neutral-800"
                      />
                    )}
                  </Field>
                  <Button
                    color="tertiary"
                    disabled={!values.domain || subdomainLoading || !dirty}
                    onClick={handleSubmit}
                  >
                    Verify Subdomain
                  </Button>
                </div>
                <div className="mt-[12px]">
                  {(subdomainSuccessful || Boolean(privacyCenterDomain)) &&
                    !subdomainLoading &&
                    !subdomainError && (
                      <div className="bg-green-100 rounded-[6px] px-[12px] py-[7px] flex items-center justify-center">
                        <div>
                          <CheckCircleIcon className="w-[16px h-[16px] text-green-400" />
                        </div>
                        <div className="ml-[12px] text-green-800 font-medium text-[12px]">
                          Great! Your subdomain is verified.
                        </div>
                      </div>
                    )}
                  {subdomainError === true && (
                    <div className="bg-red-50 rounded-[6px] px-[12px] py-[7px] flex">
                      <div className="mt-[4px]">
                        <XCircleIcon className="w-[16px h-[16px] text-red-400" />
                      </div>
                      <div className="ml-[12px] text-[12px] leading-[24px]">
                        <div className="text-red-800 font-medium">
                          Subdomain could not be verified
                        </div>
                        <div className="text-red-500 font-normal">
                          Oops! Looks like your subdomain is not set up. Please follow the
                          instructions above to set it up and retry verification.
                        </div>
                      </div>
                    </div>
                  )}
                  {subdomainError && subdomainError !== true && (
                    <div className="bg-red-50 rounded-[6px] px-[12px] py-[7px] flex">
                      <div className="mt-[4px]">
                        <XCircleIcon className="w-[16px h-[16px] text-red-400" />
                      </div>
                      <div className="ml-[12px] text-[12px]">
                        <div className="text-red-800 font-medium">
                          Subdomain could not be verified
                        </div>
                        <div className="text-red-500 font-normal">
                          Oops! Looks like {subdomainError} points to the wrong CNAME. Copy and
                          paste the value at item 7 above as the CNAME Destination and retry
                          verification.
                        </div>
                      </div>
                    </div>
                  )}
                  {subdomainError != "" && onHandleSkip && (
                    <DescriptionCheckbox
                      color="primary"
                      label="Certain DNS providers may take additional time to propagate a new CNAME record. Check this box to proceed with onboarding and verify later if your DNS propagation is going slowly."
                      onChange={(_, checked) => onHandleSkip(checked)}
                    />
                  )}
                </div>
              </div>
            </form>
          );
        }}
      />
    </>
  );
}
